<template>
  <div class="article-colored-section" :style="{ background: bgColor }">
    <div class="section-content">
      <div class="section-header">
        <h2>{{ title }}</h2>
      </div>

      <div class="section-news">
        <div class="news-grid">
          <ArticleMainCard
            v-for="item in items"
            :key="item.id"
            :slug="item.slug"
            :name="item.name"
            :image="item.image"
            :published-at="item.publicationDate"
            :link="item.link"
            class="news-card"
          />
        </div>

        <div class="view-all-button">
          <BaseButton
            name="Смотреть всё"
            theme="qiwi"
            theme-type="default"
            size="l"
            border-radius="big"
            icon-right="arrow/arrow-circle-right"
            class="custom-button"
            @click="$emit('view-all')"
          />
        </div>
      </div>
    </div>

    <div v-if="decorativeImage" class="section-decorative-image">
      <img :src="decorativeImage" :alt="title" />
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { BaseButton } from '~/components/base-ui/buttons'
  import ArticleMainCard from './ArticleMainCard.vue'
  import type { ArticleMainItem } from '~/models/articles'

  defineProps<{
    title: string
    items: ArticleMainItem[]
    bgColor: string
    decorativeImage?: string
  }>()

  defineEmits<{
    (e: 'view-all'): void
  }>()
</script>

<style lang="scss" scoped>
  .article-colored-section {
    border-radius: 24px;
    padding: 32px;
    position: relative;
    overflow: hidden;
    width: 100%;
    box-sizing: border-box;

    @include vp-767 {
      padding: 16px;
      margin-bottom: 16px;
      border-radius: 16px;
    }

    > .section-content {
      display: flex;
      align-items: flex-start;
      position: relative;
      z-index: 2;
      height: 100%;

      @include vp-1279 {
        flex-direction: column;
        gap: 20px;
      }

      > .section-header {
        @extend %flex-column;

        width: 200px;
        flex-shrink: 0;

        @include vp-1279 {
          width: 100%;
        }

        > h2 {
          font-weight: 500;
          font-size: 32px;
          line-height: 40px;
          color: $color-default-black;
          margin: 0;
          overflow-wrap: break-word;

          @include vp-767 {
            font-size: 22px;
            line-height: 26px;
          }
        }
      }

      > .section-news {
        flex: 1;
        @extend %flex-column;

        align-items: center;
        gap: 24px;
        max-width: 624px;
        width: 624px;

        @include vp-1279 {
          width: 100%;
          max-width: 100%;
        }

        > .news-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
          width: 100%;

          @include vp-767 {
            grid-template-columns: 1fr;
            gap: 12px;
          }

          > .news-card {
            :deep(.date .date-text) {
              color: $color-default-white;
            }

            :deep(.date .svoi-icon) {
              color: $color-default-white;
            }

            :deep(.content .arrow-icon) {
              color: $color-default-black;
            }
          }
        }

        > .view-all-button {
          display: flex;
          justify-content: center;

          :deep(.custom-button) {
            background: $color-default-black;
            color: $color-default-white;
          }
        }
      }
    }

    > .section-decorative-image {
      position: absolute;
      bottom: 0;
      left: 0;
      width: 228px;
      height: 228px;
      z-index: 1;

      @include vp-1279 {
        width: 160px;
        height: 120px;
      }

      @include vp-767 {
        width: 120px;
        height: 100px;
      }

      > img {
        width: 100%;
        height: 100%;
      }
    }
  }
</style>
